import * as React from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';

import { cn } from '@common/lib/utils';

function AccordionComponent({
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Root>) {
  return <AccordionPrimitive.Root data-slot="accordion" {...props} />;
}
const Accordion = React.memo(AccordionComponent);

function AccordionItemComponent({
  className,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item>) {
  return (
    <AccordionPrimitive.Item
      data-slot="accordion-item"
      className={cn('tw-border-b last:tw-border-b-0', className)}
      {...props}
    />
  );
}
const AccordionItem = React.memo(AccordionItemComponent);

interface AccordionTriggerProps extends React.ComponentProps<typeof AccordionPrimitive.Trigger> {
  icon?: React.ReactNode;
  iconClassName?: string;
  iconBefore?: React.ReactNode;
}

function AccordionTriggerComponent({
  className,
  children,
  icon,
  iconClassName,
  iconBefore,
  ...props
}: AccordionTriggerProps) {
  return (
    <AccordionPrimitive.Header className="tw-flex">
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        className={cn(
          `focus-visible:tw-border-ring focus-visible:tw-ring-ring/50 tw-flex tw-flex-1 tw-items-center tw-justify-between
          tw-gap-4 tw-rounded-md tw-py-4 tw-text-left tw-text-sm tw-font-medium tw-transition-all tw-outline-none
          focus-visible:tw-ring-[3px] disabled:tw-pointer-events-none disabled:tw-opacity-50`,
          (icon || iconBefore) && '[&[data-state=open]>span]:tw-rotate-180',
          className
        )}
        {...props}
      >
        {iconBefore && (
          <span
            className={cn(
              'tw-shrink-0 tw-transition-transform tw-duration-200',
              iconClassName
            )}
          >
            {iconBefore}
          </span>
        )}
        {children}
        {icon && (
          <span
            className={cn(
              'tw-shrink-0 tw-transition-transform tw-duration-200',
              iconClassName
            )}
          >
            {icon}
          </span>
        )}
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
}
const AccordionTrigger = React.memo(AccordionTriggerComponent);

function AccordionContentComponent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content>) {
  return (
    <AccordionPrimitive.Content
      data-slot="accordion-content"
      className={cn(`tw-overflow-hidden tw-text-sm data-[state=closed]:tw-animate-accordion-up 
        data-[state=open]:tw-animate-accordion-down`)}
      {...props}
    >
      <div className={cn('tw-pt-0 tw-pb-4', className)}>{children}</div>
    </AccordionPrimitive.Content>
  );
}
const AccordionContent = React.memo(AccordionContentComponent);

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
